import {
  lazy,
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
  type ReactElement,
} from 'react';
import { createRoutesFromElements, Navigate, Route, type RouteObject } from 'react-router-dom';

import { PageSkeleton } from '@/components/primitives/Skeleton';

import { RootLayout } from './RootLayout';
import { RouteError } from './RouteError';

/**
 * The route tree.
 *
 * A real nested `<Route>` tree, one `React.lazy` chunk per storyboard page
 * (CONVENTIONS.md §4.4). Two things are explicitly forbidden here and are
 * absent: an `OKRApp`-style god component, and any `pathname.split('/')` tab
 * dispatch — the router owns matching, `NavLink` owns active state.
 */

/** Wrap a lazy page in its own Suspense boundary so one slow chunk cannot
 *  blank a sibling, and the skeleton is the page's own shape. */
function suspended(Page: LazyExoticComponent<ComponentType>): ReactElement {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Page />
    </Suspense>
  );
}

const PulsePage = lazy(() => import('./pages/PulsePage'));
const MissionsPage = lazy(() => import('./pages/MissionsPage'));
const PlanPage = lazy(() => import('./pages/PlanPage'));
const DecisionsPage = lazy(() => import('./pages/DecisionsPage'));
const BlockersPage = lazy(() => import('./pages/BlockersPage'));
const DrillPage = lazy(() => import('./pages/DrillPage'));
const KpisPage = lazy(() => import('./pages/KpisPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const MeetingPage = lazy(() => import('./pages/MeetingPage'));
const PrintPage = lazy(() => import('./pages/PrintPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

/** The first mission drilled into when `/drill` is opened without an id. */
const DEFAULT_MISSION_ID = 'M1';

export const routes: RouteObject[] = createRoutesFromElements(
  <>
    <Route path="/" element={<RootLayout />} errorElement={<RouteError />}>
      <Route index element={<Navigate to="/pulse" replace />} />
      <Route path="pulse" element={suspended(PulsePage)} errorElement={<RouteError />} />
      <Route path="missions" element={suspended(MissionsPage)} errorElement={<RouteError />} />
      <Route path="plan" element={suspended(PlanPage)} errorElement={<RouteError />} />
      <Route path="decisions" element={suspended(DecisionsPage)} errorElement={<RouteError />} />
      <Route path="blockers" element={suspended(BlockersPage)} errorElement={<RouteError />} />
      <Route path="drill" errorElement={<RouteError />}>
        <Route index element={<Navigate to={`/drill/${DEFAULT_MISSION_ID}`} replace />} />
        <Route path=":missionId" element={suspended(DrillPage)} errorElement={<RouteError />} />
      </Route>
      <Route path="kpis" element={suspended(KpisPage)} errorElement={<RouteError />} />
      {/* Editor-only: the nav link is gated on `isEditor`, and the API refuses
          a viewer with a 403 regardless of what the SPA renders. */}
      <Route path="admin" element={suspended(AdminPage)} errorElement={<RouteError />} />
      <Route path="*" element={suspended(NotFoundPage)} />
    </Route>

    {/* OUTSIDE RootLayout, on purpose. Meeting mode is chrome-free — no
        Topbar, no vision band, no navigation — so what is on the meeting-room
        screen is the fortnight and nothing competing with it. Nesting it under
        the shell and hiding the chrome with CSS would leave the app bar in the
        accessibility tree and in the tab order, which is exactly the sort of
        thing that surfaces on the projector five minutes before the meeting. */}
    <Route path="/meeting" element={suspended(MeetingPage)} errorElement={<RouteError />} />

    {/* The pack. Outside RootLayout for the same reason as /meeting, and
        stacked rather than paged — every page of the fortnight, each on its
        own sheet. A headless renderer loads exactly this URL, so what a human
        gets from Cmd-P and what the pack generator produces are the same
        document by construction. */}
    <Route path="/print" element={suspended(PrintPage)} errorElement={<RouteError />} />
  </>
);
